import React from 'react';
import NextLink from 'next/link';
import { cn } from '@/lib/utils';

export interface LinkProps extends React.ComponentProps<typeof NextLink> {
  variant?:
    | 'default'
    | 'underline'
    | 'subtle'
    | 'ghost'
    | 'elevated'
    | 'filled'
    | 'tonal'
    | 'outlined'
    | 'text';
  color?: 'default' | 'primary' | 'accent' | 'muted' | 'destructive' | 'success' | 'warning';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  children: React.ReactNode;
}

// Base styles for each variant type
const baseStyles = {
  // Link styles
  default: 'transition-colors duration-200 font-medium',
  underline: 'transition-colors duration-200 font-medium underline-offset-4 hover:underline',
  subtle: 'transition-all duration-200 underline-offset-4 hover:underline',
  ghost: 'transition-all duration-200 underline-offset-4 hover:underline',

  // Button styles
  button:
    'inline-flex items-center justify-center rounded-md font-medium transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 active:scale-95',
};

// Full literal class strings per color/variant so Tailwind's scanner generates every one.
// Where a color has no distinct hover token, hover steps the same color down to 80%.
type VariantClassMap = Record<string, string>;

const variantClasses: Record<string, VariantClassMap> = {
  default: {
    default: 'text-foreground hover:text-accent',
    underline: 'text-foreground hover:text-accent',
    subtle: 'text-foreground/70 hover:text-accent',
    ghost: 'text-foreground/50 hover:text-accent/90',
    elevated: 'bg-surface/10 text-foreground hover:bg-surface/20 focus:ring-ring',
    filled: 'bg-surface text-surface-foreground hover:bg-surface/90 focus:ring-ring',
    tonal: 'bg-surface/10 text-foreground hover:bg-surface/20 focus:ring-ring',
    outlined: 'border border-foreground bg-transparent text-foreground hover:bg-surface/10 focus:ring-ring',
    text: 'bg-transparent text-foreground hover:bg-surface/10 focus:ring-ring',
  },
  primary: {
    default: 'text-primary hover:text-primary/80',
    underline: 'text-primary hover:text-primary/80',
    subtle: 'text-primary/70 hover:text-primary',
    ghost: 'text-primary/50 hover:text-primary/90',
    elevated: 'bg-primary/10 text-primary hover:bg-primary/20 focus:ring-primary',
    filled: 'bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-primary',
    tonal: 'bg-primary/10 text-primary hover:bg-primary/20 focus:ring-primary',
    outlined: 'border border-primary bg-transparent text-primary hover:bg-primary/10 focus:ring-primary',
    text: 'bg-transparent text-primary hover:bg-primary/10 focus:ring-primary',
  },
  accent: {
    default: 'text-accent hover:text-accent/80',
    underline: 'text-accent hover:text-accent/80',
    subtle: 'text-accent/70 hover:text-accent',
    ghost: 'text-accent/50 hover:text-accent/90',
    elevated: 'bg-accent/10 text-accent hover:bg-accent/20 focus:ring-accent',
    filled: 'bg-accent text-accent-foreground hover:bg-accent/90 focus:ring-accent',
    tonal: 'bg-accent/10 text-accent hover:bg-accent/20 focus:ring-accent',
    outlined: 'border border-accent bg-transparent text-accent hover:bg-accent/10 focus:ring-accent',
    text: 'bg-transparent text-accent hover:bg-accent/10 focus:ring-accent',
  },
  muted: {
    default: 'text-muted hover:text-foreground',
    underline: 'text-muted hover:text-foreground',
    subtle: 'text-muted/70 hover:text-foreground',
    ghost: 'text-muted/50 hover:text-foreground/90',
    elevated: 'bg-muted/10 text-muted hover:bg-muted/20 focus:ring-muted',
    filled: 'bg-muted text-muted-foreground hover:bg-muted/90 focus:ring-muted',
    tonal: 'bg-muted/10 text-muted hover:bg-muted/20 focus:ring-muted',
    outlined: 'border border-muted bg-transparent text-muted hover:bg-muted/10 focus:ring-muted',
    text: 'bg-transparent text-muted hover:bg-muted/10 focus:ring-muted',
  },
  destructive: {
    default: 'text-danger hover:text-danger/80',
    underline: 'text-danger hover:text-danger/80',
    subtle: 'text-danger/70 hover:text-danger',
    ghost: 'text-danger/50 hover:text-danger/90',
    elevated: 'bg-danger/10 text-danger hover:bg-danger/20 focus:ring-danger',
    filled: 'bg-danger text-danger-foreground hover:bg-danger/90 focus:ring-danger',
    tonal: 'bg-danger/10 text-danger hover:bg-danger/20 focus:ring-danger',
    outlined: 'border border-danger bg-transparent text-danger hover:bg-danger/10 focus:ring-danger',
    text: 'bg-transparent text-danger hover:bg-danger/10 focus:ring-danger',
  },
  success: {
    default: 'text-success hover:text-success/80',
    underline: 'text-success hover:text-success/80',
    subtle: 'text-success/70 hover:text-success',
    ghost: 'text-success/50 hover:text-success/90',
    elevated: 'bg-success/10 text-success hover:bg-success/20 focus:ring-success',
    filled: 'bg-success text-success-foreground hover:bg-success/90 focus:ring-success',
    tonal: 'bg-success/10 text-success hover:bg-success/20 focus:ring-success',
    outlined: 'border border-success bg-transparent text-success hover:bg-success/10 focus:ring-success',
    text: 'bg-transparent text-success hover:bg-success/10 focus:ring-success',
  },
  warning: {
    default: 'text-warning hover:text-warning/80',
    underline: 'text-warning hover:text-warning/80',
    subtle: 'text-warning/70 hover:text-warning',
    ghost: 'text-warning/50 hover:text-warning/90',
    elevated: 'bg-warning/10 text-warning hover:bg-warning/20 focus:ring-warning',
    filled: 'bg-warning text-warning-foreground hover:bg-warning/90 focus:ring-warning',
    tonal: 'bg-warning/10 text-warning hover:bg-warning/20 focus:ring-warning',
    outlined: 'border border-warning bg-transparent text-warning hover:bg-warning/10 focus:ring-warning',
    text: 'bg-transparent text-warning hover:bg-warning/10 focus:ring-warning',
  },
};

const sizes = {
  xs: 'text-xs px-2 py-1',
  sm: 'text-sm px-3 py-1.5',
  md: 'text-base px-4 py-2',
  lg: 'text-lg px-6 py-3',
};

const buttonVariants = new Set(['elevated', 'filled', 'tonal', 'outlined', 'text']);

function getVariantClasses(variant: string, color: string) {
  return variantClasses[color]?.[variant] ?? '';
}

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      variant = 'default',
      color = 'default',
      size = 'md',
      className,
      children,
      prefetch = false,
      ...props
    },
    ref,
  ) => {
    const isButton = buttonVariants.has(variant);

    return (
      <NextLink
        ref={ref}
        prefetch={prefetch}
        className={cn(
          'cursor-pointer',
          !isButton && 'inline-flex items-center gap-1',
          isButton ? baseStyles.button : baseStyles[variant as keyof typeof baseStyles],
          getVariantClasses(variant, color),
          isButton
            ? sizes[size]
            : `text-${size === 'xs' ? 'xs' : size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : 'base'}`,
          className,
        )}
        {...props}
      >
        {children}
      </NextLink>
    );
  },
);

Link.displayName = 'Link';
